import { ref, onBeforeUnmount } from 'vue';
import { useStatusBar } from '@ui-kit';
import { useDeviceInfo } from './useDeviceInfo';
import { Shopflow, type ScanInfo, PLUGIN_EVENTS, EVENTS } from './index';

const removeListener = ref<(() => Promise<void>) | null>(null);

export const useBarcode = ({ isTrim = true }: { isTrim?: boolean } = {}) => {
  const unsubscribeCallback = ref<(() => void) | null>(null);
  const { setBarcodeStatus } = useStatusBar();
  const { deviceInfo } = useDeviceInfo();

  const start = async () => {
    if (!deviceInfo.value?.isBarcodeScanner) return;
    if (removeListener.value) return;

    const { remove } = await Shopflow.addListener(PLUGIN_EVENTS.SCAN_BARCODE_EVENT, event => {
      setBarcodeStatus('success');
      dispatchEvent(new CustomEvent(EVENTS.SCAN_BARCODE_EVENT, { detail: event }));
    });

    removeListener.value = remove;
    Shopflow.startBarcodeTracking();
  }

  const stop = async () => {
    if (!deviceInfo.value?.isBarcodeScanner) return;

    Shopflow.stopBarcodeTracking();

    if (removeListener.value) {
      removeListener.value();
      removeListener.value = null;
    }

    unsubscribe();
  }

  const subscribe = (callback: (barcode: string) => void) => {
    if (!deviceInfo.value?.isBarcodeScanner) return;
    
    unsubscribe();

    const listener = (event: CustomEvent<ScanInfo>) => {
      callback(isTrim ? event.detail.barcode.trim() : event.detail.barcode);
    }

    window.addEventListener(EVENTS.SCAN_BARCODE_EVENT, listener);

    unsubscribeCallback.value = () => {
      window.removeEventListener(EVENTS.SCAN_BARCODE_EVENT, listener);
    }
  }

  const unsubscribe = () => {
    if (!deviceInfo.value?.isBarcodeScanner) return;

    if (unsubscribeCallback.value) {
      unsubscribeCallback.value();
      unsubscribeCallback.value = null;
    }
  }

  onBeforeUnmount(unsubscribe);

  return {
    start,
    stop,
    subscribe,
    unsubscribe
  }
}